import { useAuth } from "@clerk/clerk-react";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { fetchTransporterRunDetail, type TransporterRunDetailResponse } from "../lib/fetch-transporter-delivery-ops";
import { openSalesReceiptPdf } from "../lib/open-sales-receipt-pdf";
import { RunStatusBadge } from "../components/RunStatusBadge";
import { useTransporterWorkspace } from "../workspace/transporter-workspace-context";

export function TransporterRunDetailPage() {
  const { runId } = useParams({ strict: false }) as { runId: string };
  const { transporterId, getToken } = useTransporterWorkspace();
  const { isLoaded, isSignedIn } = useAuth();
  const [data, setData] = useState<TransporterRunDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [receiptMsg, setReceiptMsg] = useState<string | null>(null);
  const [receiptBusyId, setReceiptBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!transporterId || !runId) return;
    if (!isLoaded || !isSignedIn) return;
    setLoading(true);
    setError(null);
    try {
      const d = await fetchTransporterRunDetail(getToken, transporterId, runId);
      setData(d);
    } catch (e) {
      setData(null);
      setError(e instanceof Error ? e.message : "Failed to load run.");
    } finally {
      setLoading(false);
    }
  }, [getToken, isLoaded, isSignedIn, runId, transporterId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!transporterId) {
    return <p className="text-sm text-gray-600">Select a transporter scope to view runs.</p>;
  }

  if (loading) return <p className="text-sm text-gray-500">Loading run…</p>;
  if (error || !data) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600">{error ?? "Run not found."}</p>
        <Link to="/workspace/runs" className="text-sm text-[var(--primary,#1F7A6B)] hover:underline">
          ← All runs
        </Link>
      </div>
    );
  }

  const { run, stops } = data;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/workspace/runs" className="text-sm text-[var(--primary,#1F7A6B)] hover:underline">
            ← All runs
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-[#333333]">Run detail</h1>
          <p className="mt-1 font-mono text-xs text-gray-500">{run.id}</p>
          <p className="mt-2 text-sm text-[#555555]">{run.dispensaryLegalName}</p>
        </div>
        <RunStatusBadge status={run.status} />
      </div>

      {receiptMsg ? <p className="text-sm text-amber-800">{receiptMsg}</p> : null}

      <section className="rounded-lg border border-[var(--card-border,#e5e7eb)] bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">Metrc</h2>
        <p className="mt-1 font-mono text-xs text-gray-600">{run.metrcManifestNumber ?? "—"}</p>
        <p className="mt-1 text-xs text-gray-500">{run.manifestStatus}</p>
      </section>

      <section className="rounded-lg border border-[var(--card-border,#e5e7eb)] bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">Stops</h2>
        {stops.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">No stops.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Seq</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Delivered</th>
                  <th className="px-3 py-2">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {stops.map((s) => (
                  <tr key={s.id} className="border-b border-gray-100 last:border-0">
                    <td className="px-3 py-2 tabular-nums">{s.sequenceNumber}</td>
                    <td className="px-3 py-2">{s.status}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600">
                      {s.deliveredAt ? s.deliveredAt.slice(0, 19).replace("T", " ") : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {s.deliveredAt ? (
                        <button
                          type="button"
                          disabled={receiptBusyId !== null}
                          className="text-xs font-medium text-[var(--primary,#1F7A6B)] hover:underline disabled:opacity-50"
                          onClick={() => {
                            setReceiptMsg(null);
                            setReceiptBusyId(s.id);
                            void (async () => {
                              const r = await openSalesReceiptPdf(getToken, s.id);
                              setReceiptBusyId(null);
                              if (!r.ok) setReceiptMsg(r.message);
                            })();
                          }}
                        >
                          {receiptBusyId === s.id ? "Opening…" : "Download PDF"}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
